import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaPaperPlane, FaTimes, FaCheckCircle } from 'react-icons/fa';
import { sendSubmission } from '../utils/mailer';
import './Chatbot.css';

// ─── Proven InfoSec Knowledge Base ────────────────────────────────────────────
// Trained on all website content: services, about, locations, compliance, etc.

const COMPANY = {
  name: 'Proven InfoSec',
  tagline: 'Your Security Partner Across India & the UK',
  ranking: '24/7 Enterprise Security & Compliance Partner across India & the UK',
  mission: 'Empowering organizations through secure technology — delivering cybersecurity, managed IT, and secure software development under one roof.',
  vision: 'A unified approach to technology services that eliminates gaps between security, IT operations, and software engineering.',
  experience: '20+ years',
  assessments: '500+',
  professionals: 'a growing team of certified professionals',
  uptime: 'SLA-backed',
  certifications: ['CISSP', 'CEH', 'OSCP', 'CISA', 'ISO 27001 Lead Auditor', 'CSA (Certified SOC Analyst)'],
  offices: {
    uk: 'Unit 5, Mansfield House, 22 Northgate, Sleaford, NG34 7DA, United Kingdom',
    india: 'KOIKKARA BUILDING MAYILADUMKUNNU, P.O, Kizhakkambalam, Kerala 683562, India',
  },
  values: ['Security First', 'Technical Excellence', 'Partnership Mindset', 'Transparency', 'Continuous Improvement'],
};

// Intent matching patterns and responses
const INTENTS = [
  // ─── Greetings ──────────────────────────────────────────────────────────────
  {
    patterns: ['hello', 'hi', 'hey', 'good morning', 'good afternoon', 'good evening', 'greetings', 'howdy'],
    response: () => (
      <span>
        Hello! Welcome to <strong>Proven InfoSec</strong> — {COMPANY.ranking}. I am your AI Security Assistant, available 24/7. How can I assist you with your cybersecurity, compliance, or IT operations needs today?
      </span>
    ),
  },

  // ─── Company / About ────────────────────────────────────────────────────────
  {
    patterns: ['about', 'who are you', 'company', 'proven infosec', 'what do you do', 'tell me about', 'your company'],
    response: () => (
      <span>
        <strong>Proven InfoSec</strong> is a cybersecurity firm led by practitioners with {COMPANY.experience} of industry experience, serving as a {COMPANY.ranking}. We operate three core divisions: <strong>Cybersecurity Services</strong>, <strong>Managed IT</strong>, and <strong>Secure Software Development</strong>. With {COMPANY.professionals} (CISSP, CEH, OSCP, CISA certified), {COMPANY.assessments} security assessments delivered, and offices in the <strong>UK</strong> and <strong>India</strong>, we serve mid-market and enterprise clients globally. Learn more on our <Link to="/about" className="chat-link">About Page</Link>.
      </span>
    ),
  },

  // ─── Mission / Vision ───────────────────────────────────────────────────────
  {
    patterns: ['mission', 'vision', 'purpose', 'why proven'],
    response: () => (
      <span>
        <strong>Our Mission:</strong> {COMPANY.mission}<br /><br />
        <strong>Our Vision:</strong> {COMPANY.vision}<br /><br />
        Our core values: {COMPANY.values.join(', ')}. Visit our <Link to="/about" className="chat-link">About Page</Link> for more.
      </span>
    ),
  },

  // ─── VAPT ───────────────────────────────────────────────────────────────────
  {
    patterns: ['vapt', 'vulnerability assessment', 'penetration testing', 'pen test', 'pentest', 'vulnerability scan'],
    response: () => (
      <span>
        Our <strong>VAPT</strong> services combine automated scanning with deep manual exploitation by certified expert testers (CISSP, CEH, OSCP). We deliver zero false positives — every finding is manually validated. Our methodology: <strong>Scoping &rarr; Reconnaissance &rarr; Exploitation &rarr; Reporting</strong> with a complimentary 30-day retest. We cover web apps, APIs, networks, mobile apps, and cloud environments. VAPT is critical for ISO 27001, NIST CSF 2.0, and CIS Controls compliance. Request a scoped proposal on our <Link to="/contact" className="chat-link">Contact Page</Link>.
      </span>
    ),
  },

  // ─── Web Application Security ──────────────────────────────────────────────
  {
    patterns: ['web app', 'web application', 'owasp', 'xss', 'sql injection', 'web security', 'web pen'],
    response: () => (
      <span>
        Our <strong>Web Application Penetration Testing</strong> follows OWASP Top 10 methodology covering injection, broken authentication, XSS, SSRF, and business logic flaws. We go beyond automated scanning to test multi-tenancy bypasses, privilege escalation, and session management. Every vulnerability includes safe proof-of-concept steps and code-level remediation advice. We have hardened <strong>500+ applications</strong>. View details at <Link to="/services/web-application-penetration-testing" className="chat-link">Web App Testing</Link>.
      </span>
    ),
  },

  // ─── Mobile App Testing ────────────────────────────────────────────────────
  {
    patterns: ['mobile', 'ios', 'android', 'mobile app', 'mobile penetration', 'mobile security'],
    response: () => (
      <span>
        Our <strong>Mobile App Penetration Testing</strong> covers both iOS and Android following the <strong>OWASP MASTG</strong> framework. We perform static binary analysis, dynamic runtime testing, certificate pinning bypass, and full backend API audits. We test iOS Keychain, Android Keystore, local caches, IPC channels, and reverse engineering defenses. Remediation guides are tailored to Swift/Objective-C and Kotlin/Java. Details at <Link to="/services/mobile-app-penetration-testing" className="chat-link">Mobile Testing</Link>.
      </span>
    ),
  },

  // ─── API Security ──────────────────────────────────────────────────────────
  {
    patterns: ['api', 'rest', 'graphql', 'grpc', 'api security', 'api pen', 'bola', 'idor'],
    response: () => (
      <span>
        API security is a core part of our <strong>Web Application Security Testing</strong>. We test REST, GraphQL, and gRPC endpoints for BOLA/IDOR, broken function-level authorization, token/JWT validation, OAuth flow issues, and rate limiting — reviewing OpenAPI/Swagger specs for full coverage. Details at <Link to="/services/web-application-penetration-testing" className="chat-link">Web Application Security Testing</Link>.
      </span>
    ),
  },

  // ─── Secure SDLC / Code Review ─────────────────────────────────────────────
  {
    patterns: ['code review', 'source code', 'sast', 'static analysis', 'secure code', 'code audit', 'secure sdlc', 'sdlc'],
    response: () => (
      <span>
        Secure code review and SAST/DAST/SCA integration are delivered through our <strong>Secure SDLC</strong> practice — we embed automated security checks into your pipeline and pair them with expert manual review of authentication, cryptography, and secrets handling. Details on our <Link to="/development" className="chat-link">Development</Link> page.
      </span>
    ),
  },

  // ─── Network Security ──────────────────────────────────────────────────────
  {
    patterns: ['network', 'internal', 'external', 'firewall', 'active directory', 'lateral movement', 'wireless', 'wifi'],
    response: () => (
      <span>
        Our <strong>Network Penetration Testing</strong> covers external perimeter audits, internal lateral movement simulation, Active Directory hardening, and segment validation. We test VPNs, firewalls, remote access systems, and simulate AD compromise to identify privilege escalation paths. We have audited <strong>500+ networks</strong> with zero operational disruption. We also offer <strong>Wireless Penetration Testing</strong> and <strong>Firewall Security Audits</strong>. Details at <Link to="/services/network-penetration-testing" className="chat-link">Network Testing</Link>.
      </span>
    ),
  },

  // ─── Cloud Security ────────────────────────────────────────────────────────
  {
    patterns: ['cloud', 'aws', 'azure', 'gcp', 'cloud security', 'cloud audit', 's3', 'iam', 'misconfiguration'],
    response: () => (
      <span>
        Our <strong>Cloud Security Audit</strong> covers AWS, Azure, and GCP — including IAM least-privilege audits, public storage detection, network security group analysis, and CIS benchmark compliance. We also review Terraform, CloudFormation, and Bicep IaC templates. Misconfiguration is the #1 cause of cloud breaches. We offer hardening services for <strong>AWS</strong>, <strong>Azure</strong>, <strong>GCP</strong>, <strong>WordPress</strong>, and <strong>Container/Kubernetes</strong> environments. Details at <Link to="/services/cloud-security-audit" className="chat-link">Cloud Security</Link>.
      </span>
    ),
  },

  // ─── ISO 27001 ─────────────────────────────────────────────────────────────
  {
    patterns: ['iso 27001', 'iso27001', 'isms', 'iso certification', 'information security management'],
    response: () => (
      <span>
        Our <strong>ISO 27001 Consulting</strong> delivers end-to-end ISMS implementation — from gap assessment and risk framework design to control rollout and certification support. We maintain a <strong>100% audit success rate</strong> and achieve readiness <strong>2x faster</strong> than industry average. ISO 27001 certification unlocks enterprise deals and demonstrates due diligence. Our advisors are ISO Lead Auditors. Details at <Link to="/services/iso-27001-consulting" className="chat-link">ISO 27001</Link>.
      </span>
    ),
  },

  // ─── Security Frameworks (NIST / CIS / vCISO) ──────────────────────────────
  {
    patterns: ['nist', 'nist csf', 'csf', 'cis', 'cis controls', 'framework', 'vciso', 'virtual ciso', 'nist ai', 'ai rmf', 'grc', 'pci', 'payment', 'cardholder'],
    response: () => (
      <span>
        We implement the security frameworks we deliver directly: <strong>ISO 27001</strong>, <strong>NIST CSF 2.0</strong>, <strong>CIS Controls v8.1</strong>, and the <strong>NIST AI RMF</strong> — with maturity baselining, control implementation, and a prioritized roadmap. Need leadership to own it? Our <strong>vCISO</strong> runs your whole GRC program. If card-data security is your driver, our web application and network penetration testing plus CIS Controls hardening cover the technical controls behind PCI (we are not a PCI QSA). Explore <Link to="/services/nist-csf-implementation" className="chat-link">NIST CSF 2.0</Link>, <Link to="/services/cis-controls-implementation" className="chat-link">CIS Controls</Link>, and <Link to="/services/vciso-services" className="chat-link">vCISO Services</Link>.
      </span>
    ),
  },

  // ─── GDPR / Privacy ────────────────────────────────────────────────────────
  {
    patterns: ['gdpr', 'privacy', 'data protection', 'dpia', 'sar', 'personal data', 'dpo', 'dpdp'],
    response: () => (
      <span>
        Our <strong>Data Privacy</strong> services include GDPR and UK GDPR compliance and DPDP Act compliance for India. We perform PII data mapping, Data Protection Impact Assessments (DPIAs), privacy policy drafting, and Subject Access Request (SAR) management. Compliance protects against regulatory fines and builds customer trust. Details at <Link to="/services/gdpr-compliance" className="chat-link">GDPR Compliance</Link>.
      </span>
    ),
  },

  // ─── General Compliance ────────────────────────────────────────────────────
  {
    patterns: ['compliance', 'audit', 'regulation', 'soc 2', 'certification'],
    response: () => (
      <span>
        Proven InfoSec covers a focused compliance portfolio: <strong>ISO 27001</strong>, <strong>NIST CSF 2.0</strong>, <strong>CIS Controls</strong>, <strong>NIST AI RMF</strong>, <strong>GDPR / UK GDPR</strong>, <strong>DPDP</strong>, and <strong>Cyber Essentials & CE+</strong> — backed by <strong>vCISO</strong> program leadership. Our gap assessments, roadmap planning, control implementation, internal audit, and certification support ensure a <strong>100% ISO 27001 audit success rate</strong>. Compliance is not just a checkbox — it unlocks enterprise deals, reduces breach liability, and builds lasting trust. Explore all services at <Link to="/services" className="chat-link">Services Page</Link>.
      </span>
    ),
  },

  // ─── Managed IT ────────────────────────────────────────────────────────────
  {
    patterns: ['managed it', 'endpoint management', 'patch management', 'backup', 'disaster recovery', 'helpdesk', 'it support', 'it operations'],
    response: () => (
      <span>
        Our <strong>Managed IT Services</strong> are designed with a security-first mindset — not generic helpdesk support. We cover <strong>Endpoint Management</strong> (EDR, policy enforcement, remote wipe), <strong>Server & Network Monitoring</strong>, <strong>Patch Management</strong>, <strong>Backup & Disaster Recovery</strong> (ransomware-resilient), <strong>Identity & Access Management</strong> (Azure AD, SSO, MFA), <strong>Cloud Infrastructure Support</strong> (AWS, Azure, GCP), and <strong>SLA-driven Remote Helpdesk</strong>. Availability and response are <strong>backed by your SLA</strong>. Details at <Link to="/managed-it" className="chat-link">Managed IT Page</Link>.
      </span>
    ),
  },

  // ─── SOC / Managed Security ────────────────────────────────────────────────
  {
    patterns: ['soc', 'security operations', 'siem', 'monitoring', '24/7', 'threat detection', 'incident response', 'managed security'],
    response: () => (
      <span>
        Our <strong>Security Operations Centre (SOC)</strong> provides 24/7 SIEM monitoring, threat detection, alert triage, and analyst-led incident coordination. Response times are <strong>backed by your SLA</strong>. We offer <strong>Managed Security Services</strong>, <strong>Annual Security Programs</strong>, <strong>Managed Threat Hunting</strong>, and <strong>Managed Vulnerability Scanning</strong>. Our SOC never sleeps — giving you expert coverage without the headcount cost. Details at <Link to="/services/security-operations-centre" className="chat-link">SOC Services</Link>.
      </span>
    ),
  },

  // ─── Software Development ──────────────────────────────────────────────────
  {
    patterns: ['development', 'software', 'custom app', 'web development', 'mobile development', 'react', 'next.js', 'ai development', 'automation'],
    response: () => (
      <span>
        Our <strong>Secure Software Development</strong> division builds custom applications, AI solutions, and security dashboards with security-first architecture. Tech stack: <strong>React, Next.js, TypeScript, Node.js, Python, .NET, Go, React Native, Flutter, PyTorch, TensorFlow</strong>. We integrate <strong>SAST/DAST/SCA</strong> security testing into CI/CD pipelines. Our six-stage process: Discovery &rarr; Design &rarr; Develop &rarr; Test &rarr; Deploy &rarr; Support. Not a dev shop — a security-focused engineering partner. Details at <Link to="/development" className="chat-link">Development Page</Link>.
      </span>
    ),
  },

  // ─── DevSecOps / Pipeline ──────────────────────────────────────────────────
  {
    patterns: ['devsecops', 'ci/cd', 'pipeline', 'deployment gates'],
    response: () => (
      <span>
        We embed automated security into your CI/CD pipeline — SAST, DAST, SCA tooling, and deployment gates — as part of our <strong>Secure SDLC</strong> practice, so every release ships secure by default. Details on our <Link to="/development" className="chat-link">Development</Link> page.
      </span>
    ),
  },

  // ─── Security Leadership / Consulting ──────────────────────────────────────
  {
    patterns: ['vciso', 'virtual ciso', 'ciso', 'security leadership', 'board reporting', 'security strategy', 'consulting'],
    response: () => (
      <span>
        Our <strong>Security Consulting</strong> gives you executive-level security guidance on demand — strategic roadmaps, risk registers, maturity assessments, and board-level reporting, led by CISSP and CISA certified advisors. Details at <Link to="/services/cyber-security-strategic-consulting" className="chat-link">Security Consulting</Link>.
      </span>
    ),
  },

  // ─── Awareness / Phishing readiness ────────────────────────────────────────
  {
    patterns: ['social engineering', 'phishing', 'vishing', 'security awareness', 'human firewall', 'employee training', 'awareness training'],
    response: () => (
      <span>
        Over <strong>90% of breaches</strong> start with phishing. We strengthen your human layer through <strong>Trinex360 Learn</strong> — our security awareness training platform with scenario-led courses, gamified phishing and fraud simulations, and automatic certification. Explore it on our <Link to="/products" className="chat-link">Products Page</Link>.
      </span>
    ),
  },

  // ─── E-commerce / SaaS (industries) ────────────────────────────────────────
  {
    patterns: ['ecommerce', 'e-commerce', 'saas', 'storefront', 'checkout', 'multi-tenant'],
    response: () => (
      <span>
        E-commerce and SaaS are two of the industries we specialize in — with web application security testing, ISO 27001 and NIST CSF readiness, and 24/7 monitoring tailored to each. See <Link to="/industries/ecommerce-retail" className="chat-link">E-commerce & Retail</Link> and <Link to="/industries/saas-technology" className="chat-link">SaaS & Technology</Link>.
      </span>
    ),
  },

  // ─── Pricing / Quote ───────────────────────────────────────────────────────
  {
    patterns: ['cost', 'price', 'pricing', 'quote', 'how much', 'budget', 'proposal', 'estimate'],
    response: () => (
      <span>
        Pricing is scope-dependent and tailored to your specific requirements — number of application endpoints, active network IPs, compliance standards, or managed service tiers. We provide detailed, transparent proposals within <strong>one business day</strong>. For a fast, no-obligation quote, please submit your requirements on our <Link to="/contact" className="chat-link">Contact Page</Link>.
      </span>
    ),
  },

  // ─── Contact / Location ────────────────────────────────────────────────────
  {
    patterns: ['contact', 'reach', 'email', 'phone', 'talk', 'call', 'location', 'office', 'address', 'where'],
    response: () => (
      <span>
        We operate from two offices:<br />
        <strong>UK:</strong> {COMPANY.offices.uk}<br />
        <strong>India:</strong> {COMPANY.offices.india}<br /><br />
        We respond to every inquiry within <strong>one business day</strong>. Reach us directly on our <Link to="/contact" className="chat-link">Contact Page</Link>.
      </span>
    ),
  },

  // ─── Certifications / Team ─────────────────────────────────────────────────
  {
    patterns: ['certification', 'certified', 'cissp', 'crest', 'cism', 'team', 'expertise', 'qualif'],
    response: () => (
      <span>
        Our team holds industry-leading certifications: <strong>{COMPANY.certifications.join(', ')}</strong>. We are <strong>{COMPANY.professionals}</strong>, led by practitioners with <strong>{COMPANY.experience}</strong> of industry experience, bringing deep technical expertise to every engagement. Our testers, architects, and advisors are practitioners — not just consultants. Learn more on our <Link to="/about" className="chat-link">About Page</Link>.
      </span>
    ),
  },

  // ─── Container / Kubernetes ────────────────────────────────────────────────
  {
    patterns: ['container', 'kubernetes', 'docker', 'k8s', 'cluster'],
    response: () => (
      <span>
        Container and Kubernetes security — image scanning, runtime protection, pod and network policies, RBAC, and secrets management — are covered within our <strong>Cloud Security</strong> assessments. Details at <Link to="/services/cloud-security-audit" className="chat-link">Cloud Security</Link>.
      </span>
    ),
  },

  // ─── Email / M365 Security ─────────────────────────────────────────────────
  {
    patterns: ['email', 'microsoft 365', 'm365', 'office 365', 'email security', 'spoofing'],
    response: () => (
      <span>
        We offer <strong>Email Security</strong> and <strong>Microsoft 365 Security</strong> hardening. Email security covers secure email gateway implementation (enterprise solutions or our own), Microsoft 365 / Google Workspace integration, SPF/DKIM/DMARC enforcement, and continuous management and monitoring against phishing, BEC and malware. M365 security covers identity, data protection, and threat protection across your tenant. Details at <Link to="/services/email-security-audit" className="chat-link">Email Security</Link> and <Link to="/services/microsoft-365-security" className="chat-link">M365 Security</Link>.
      </span>
    ),
  },

  // ─── Zero Trust ────────────────────────────────────────────────────────────
  {
    patterns: ['zero trust', 'zerotrust', 'least privilege', 'identity-first'],
    response: () => (
      <span>
        Our <strong>Zero Trust Architecture</strong> service designs and implements identity-first security — every user, device, and request is verified before access is granted, with least-privilege enforced across your environment. Details at <Link to="/services/zero-trust-architecture" className="chat-link">Zero Trust Architecture</Link>.
      </span>
    ),
  },

  // ─── Hardening / CIS Benchmarks ────────────────────────────────────────────
  {
    patterns: ['server hardening', 'harden', 'cis benchmark', 'hardening'],
    response: () => (
      <span>
        We harden servers and cloud workloads to CIS and best-practice benchmarks — closing default configs, excessive permissions, and unpatched services — as part of our <strong>Cloud Security</strong> and <strong>Managed IT</strong> engagements. See <Link to="/services/cloud-security-audit" className="chat-link">Cloud Security</Link> or our <Link to="/managed-it" className="chat-link">Managed IT</Link> page.
      </span>
    ),
  },

  // ─── Security Architecture ─────────────────────────────────────────────────
  {
    patterns: ['architecture', 'security architecture', 'design review', 'architecture review'],
    response: () => (
      <span>
        Our <strong>Security Architecture Review</strong> provides expert evaluation of your security design — identifying gaps in network segmentation, identity management, data protection, and resilience. We help you build a target-state architecture that withstands modern threats. Details at <Link to="/services/security-architecture-review" className="chat-link">Architecture Review</Link>.
      </span>
    ),
  },

  // ─── Products (Trinex) ─────────────────────────────────────────────────────
  {
    patterns: ['trinex', 'exposure 360', 'product', 'attack surface monitoring', 'exposure monitoring', 'dark web monitoring'],
    response: () => (
      <span>
        We build two products of our own. <strong>Trinex Exposure 360</strong> is our external
        exposure monitoring platform — agentless, outside-in scanning of your attack surface, dark
        web and ransomware leak-site monitoring, credential leak detection, and third-party risk
        scoring, all rolled into a board-ready risk grade with India and UK data residency.
        {' '}<strong>Trinex360 Learn</strong> is our security awareness training platform —
        scenario-led courses, gamified simulations, and automatic certification. Explore both on
        our <Link to="/products" className="chat-link">Products Page</Link>.
      </span>
    ),
  },

  // ─── Services Overview ─────────────────────────────────────────────────────
  {
    patterns: ['services', 'what services', 'full list', 'all services', 'what you offer', 'offerings'],
    response: () => (
      <span>
        Proven InfoSec offers specialized security services across 8 categories:<br /><br />
        <strong>Application Security</strong> — VAPT, Web/Mobile/API Testing, Code Review, DevSecOps<br />
        <strong>Network Security</strong> — Network/Wireless Testing, Firewall Audits<br />
        <strong>Cloud Security</strong> — AWS/Azure/GCP Audits, Container Security, WordPress<br />
        <strong>Cyber Risk Management</strong> — ISO 27001, NIST CSF 2.0, CIS Controls, NIST AI RMF<br />
        <strong>Data Privacy</strong> — GDPR / UK GDPR, DPO as a Service, DPDP<br />
        <strong>Managed Services</strong> — 24/7 SOC, Incident Response, Threat Hunting, Vulnerability Scanning<br />
        <strong>Enterprise Security</strong> — vCISO, Zero Trust, M365, Email, Server Hardening<br />
        <strong>Threat Simulations</strong> — Phishing, Vishing, Physical Testing<br /><br />
        Explore all at <Link to="/services" className="chat-link">Services Page</Link>.
      </span>
    ),
  },

  // ─── Thank you / Goodbye ───────────────────────────────────────────────────
  {
    patterns: ['thank', 'thanks', 'bye', 'goodbye', 'see you', 'appreciate'],
    response: () => (
      <span>
        You're welcome! Remember — security is a continuous journey, not a destination. Proven InfoSec is here 24/7 whenever you need expert guidance. Stay secure.
      </span>
    ),
  },
];

// ─── Strict Security Mode: Off-Topic Deflection ──────────────────────────────
const SECURITY_DEFLECTION = () => (
  <span>
    <strong>Security Mode Active.</strong> I am trained exclusively on Proven InfoSec's cybersecurity, compliance, managed IT, and secure development services. I can only assist with security-related inquiries. For topics outside this scope, I recommend consulting the appropriate resource.<br /><br />
    If you have a security question, try asking about our <strong>VAPT</strong>, <strong>compliance programs</strong>, <strong>managed IT</strong>, <strong>cloud security</strong>, or <strong>software development</strong> services. Or reach our team directly on our <Link to="/contact" className="chat-link">Contact Page</Link>.
  </span>
);

// ─── Off-topic patterns to actively deflect ──────────────────────────────────
const OFFTOPIC_PATTERNS = [
  'weather', 'joke', 'funny', 'game', 'play', 'movie', 'music', 'food', 'recipe',
  'sports', 'football', 'cricket', 'news', 'politics', 'stock', 'crypto', 'bitcoin',
  'dating', 'love', 'relationship', 'homework', 'essay', 'write me', 'poem',
  'story', 'fiction', 'chat gpt', 'openai', 'gemini', 'claude',
];

const getBotResponse = (text) => {
  const query = text.toLowerCase().trim();

  // Check off-topic first (strict security mode)
  if (OFFTOPIC_PATTERNS.some((p) => query.includes(p))) {
    return SECURITY_DEFLECTION();
  }

  // Match against knowledge base intents
  for (const intent of INTENTS) {
    if (intent.patterns.some((p) => query.includes(p))) {
      return intent.response();
    }
  }

  // Fallback — still security-focused
  return (
    <span>
      I appreciate your question. While I may not have a specific answer for that query, our certified security professionals can provide detailed guidance. Please share your inquiry on our <Link to="/contact" className="chat-link">Contact Page</Link> — we respond within <strong>one business day</strong>.<br /><br />
      You can also ask me about: <strong>VAPT</strong>, <strong>ISO 27001</strong>, <strong>cloud security</strong>, <strong>managed IT</strong>, <strong>SOC monitoring</strong>, <strong>DevSecOps</strong>, or any of our <strong>40+ security services</strong>.
    </span>
  );
};

// ─── Typewriter Effect Helpers & Component ───────────────────────────────────

const pruneTree = (node, targetCount, state = { count: 0 }) => {
  if (node == null) return null;
  if (state.count >= targetCount) {
    return null;
  }

  if (typeof node === 'string') {
    const remaining = targetCount - state.count;
    if (node.length <= remaining) {
      state.count += node.length;
      return node;
    } else {
      const sliced = node.slice(0, remaining);
      state.count = targetCount;
      return sliced;
    }
  }

  if (typeof node === 'number') {
    const s = String(node);
    const remaining = targetCount - state.count;
    if (s.length <= remaining) {
      state.count += s.length;
      return node;
    } else {
      const sliced = s.slice(0, remaining);
      state.count = targetCount;
      return sliced;
    }
  }

  if (React.isValidElement(node)) {
    if (!node.props || !node.props.children) {
      return node;
    }
    
    const children = React.Children.toArray(node.props.children);
    const prunedChildren = [];
    
    for (const child of children) {
      const pruned = pruneTree(child, targetCount, state);
      if (pruned !== null) {
        prunedChildren.push(pruned);
      }
      if (state.count >= targetCount) {
        break;
      }
    }
    
    return React.cloneElement(node, { ...node.props, key: node.key }, prunedChildren);
  }

  if (Array.isArray(node)) {
    const prunedArray = [];
    for (const item of node) {
      const pruned = pruneTree(item, targetCount, state);
      if (pruned !== null) {
        prunedArray.push(pruned);
      }
      if (state.count >= targetCount) {
        break;
      }
    }
    return prunedArray;
  }

  return node;
};

const TypewriterText = ({ children, onComplete, onCharacterTyped }) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const totalCharsRef = useRef(0);
  
  useEffect(() => {
    let chars = 0;
    const countChars = (node) => {
      if (node == null) return;
      if (typeof node === 'string' || typeof node === 'number') {
        chars += String(node).length;
      } else if (React.isValidElement(node)) {
        if (node.props && node.props.children) {
          React.Children.forEach(node.props.children, countChars);
        }
      } else if (Array.isArray(node)) {
        node.forEach(countChars);
      }
    };
    countChars(children);
    totalCharsRef.current = chars;
  }, [children]);

  useEffect(() => {
    if (visibleCount >= totalCharsRef.current) {
      if (onComplete) onComplete();
      return;
    }

    const timer = setTimeout(() => {
      const step = 1;
      setVisibleCount((prev) => {
        const next = Math.min(prev + step, totalCharsRef.current);
        if (onCharacterTyped) {
          requestAnimationFrame(onCharacterTyped);
        }
        return next;
      });
    }, 35);

    return () => clearTimeout(timer);
  }, [visibleCount, children, onComplete, onCharacterTyped]);

  const pruned = pruneTree(children, visibleCount);
  const isTyping = visibleCount < totalCharsRef.current;

  return (
    <span className="typewriter-container">
      {pruned}
      {isTyping && <span className="typewriter-cursor"></span>}
    </span>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: 'bot',
      text: (
        <span>
          Welcome to <strong>Proven InfoSec</strong> — {COMPANY.ranking}. I am your <strong>AI Security Assistant</strong>, available 24/7 in strict security mode. How can I help you with cybersecurity, compliance, or IT operations today?
        </span>
      ),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Lead capture — collect contact details and email them via the shared API.
  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState({ name: '', email: '', message: '' });
  const [leadSending, setLeadSending] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [leadError, setLeadError] = useState('');

  const submitLead = async (e) => {
    e.preventDefault();
    if (leadSending) return;
    setLeadError('');
    setLeadSending(true);
    try {
      await sendSubmission({
        source: 'Chatbot — Talk to an Expert',
        fields: {
          Name: lead.name,
          Email: lead.email,
          Message: lead.message || '(via chatbot — requested a callback)',
        },
        replyTo: lead.email,
      });
      setLeadSent(true);
    } catch (err) {
      setLeadError('Could not send just now — please try again or email info@proveninfosec.com.');
    } finally {
      setLeadSending(false);
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  const toggleChat = () => setIsOpen(!isOpen);

  const handleSendMessage = (text) => {
    if (!text.trim()) return;

    const userMsg = {
      sender: 'user',
      text: text,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);
      const botReply = {
        sender: 'bot',
        text: getBotResponse(text),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        shouldType: true,
      };
      setMessages((prev) => [...prev, botReply]);
    }, 1200);
  };

  return (
    <div className="chatbot-wrapper">
      <button
        className={`chatbot-launcher ${isOpen ? 'active' : ''}`}
        onClick={toggleChat}
        aria-label="Open support chat"
      >
        <img
          src={`${process.env.PUBLIC_URL}/botlogo.png`}
          alt="Chat Bot Logo"
          className="chatbot-launcher-img"
        />
        <span className="chatbot-pulse-dot" />
      </button>

      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar">
                <img
                  src={`${process.env.PUBLIC_URL}/logo.png`}
                  alt="Assistant Avatar"
                  className="chatbot-avatar-img"
                />
                <span className="chatbot-online-indicator" />
              </div>
              <div>
                <h3 className="chatbot-title">
                  Proven Assistant
                </h3>
                <span className="chatbot-subtitle">Online · 24/7 · Strict Security Mode</span>
              </div>
            </div>
            <button className="chatbot-close-btn" onClick={toggleChat} aria-label="Close chat">
              <FaTimes />
            </button>
          </div>

          <div className="chatbot-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`chat-message ${msg.sender === 'user' ? 'user' : 'bot'}`}>
                <div className="chat-message-bubble">
                  <div className="chat-message-text">
                    {msg.sender === 'bot' && msg.shouldType ? (
                      <TypewriterText
                        onComplete={() => {
                          setMessages((prev) =>
                            prev.map((m, idx) =>
                              idx === index ? { ...m, shouldType: false } : m
                            )
                          );
                        }}
                        onCharacterTyped={() => {
                          if (messagesEndRef.current) {
                            messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
                          }
                        }}
                      >
                        {msg.text}
                      </TypewriterText>
                    ) : (
                      msg.text
                    )}
                  </div>
                  <span className="chat-message-time">{msg.time}</span>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="chat-message bot typing">
                <div className="chat-message-bubble">
                  <div className="typing-dots">
                    <span /><span /><span />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-suggestions">
            <button className="chat-suggestion-chip" onClick={() => handleSendMessage('What VAPT services do you offer?')}>
              VAPT Services
            </button>
            <button className="chat-suggestion-chip" onClick={() => handleSendMessage('Tell me about ISO 27001 compliance')}>
              ISO 27001
            </button>
            <button className="chat-suggestion-chip" onClick={() => handleSendMessage('What cloud security audits do you provide?')}>
              Cloud Security
            </button>
            <button className="chat-suggestion-chip" onClick={() => handleSendMessage('Tell me about your Managed IT services')}>
              Managed IT
            </button>
            <button className="chat-suggestion-chip" onClick={() => { setLeadSent(false); setLeadOpen(true); }}>
              Talk to Expert
            </button>
          </div>

          {leadOpen && (
            <div className="chatbot-lead">
              {!leadSent ? (
                <form onSubmit={submitLead}>
                  <div className="chatbot-lead-head">
                    <strong>Talk to a security expert</strong>
                    <button type="button" aria-label="Close" onClick={() => setLeadOpen(false)}>
                      <FaTimes />
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Your name"
                    required
                    value={lead.name}
                    onChange={(e) => setLead({ ...lead, name: e.target.value })}
                  />
                  <input
                    type="email"
                    placeholder="Work email"
                    required
                    value={lead.email}
                    onChange={(e) => setLead({ ...lead, email: e.target.value })}
                  />
                  <textarea
                    placeholder="What do you need help with? (optional)"
                    rows="2"
                    value={lead.message}
                    onChange={(e) => setLead({ ...lead, message: e.target.value })}
                  />
                  <button type="submit" className="chatbot-lead-btn" disabled={leadSending}>
                    {leadSending ? 'Sending…' : 'Request a callback'}
                  </button>
                  {leadError && <p className="chatbot-lead-error">{leadError}</p>}
                </form>
              ) : (
                <div className="chatbot-lead-sent">
                  <FaCheckCircle /> Thanks — our team will reach out shortly.
                </div>
              )}
            </div>
          )}

          <form
            className="chatbot-input-area"
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage(inputValue);
            }}
          >
            <input
              type="text"
              placeholder="Ask a security question..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="chatbot-input-field"
            />
            <button type="submit" className="chatbot-send-btn" aria-label="Send message">
              <FaPaperPlane />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
